// components/brvm/StockSheet.jsx — Fiche détaillée d'une valeur BRVM (overlay).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Star, Bell } from "lucide-react";
import { fetchStock, fmtNum, fmtPct, deltaColor, deltaArrow, tileColor, tileInitials, toggleWatch } from "./brvmApi";
import BrvmChart from "./BrvmChart";

const CARD = { borderRadius: 12, background: "rgba(8,38,24,0.75)", border: "1px solid rgba(16,185,129,0.22)" };

function Stat({ label, value, unavailable }) {
  return (
    <div style={{ ...CARD, padding: "8px 11px" }}>
      <div style={{ fontSize: 10, color: "#9DB4A6", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: unavailable ? 11 : 14.5, fontWeight: unavailable ? 600 : 800, color: unavailable ? "#6B7F72" : "#ECF5EE", marginTop: 2 }}>
        {unavailable ? "Donnée indisponible" : value}
      </div>
    </div>
  );
}

export default function StockSheet({ symbol, followed, onToggleFollow, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null); setError(false);
    fetchStock(symbol).then((d) => (d.ok ? setData(d) : setError(true))).catch(() => setError(true));
  }, [symbol]);

  const s = data?.stock;
  const chartPoints = (data?.history || [])
    .filter((h) => Number.isFinite(h.close))
    .map((h) => ({ ts: `${h.day}T16:00:00Z`, value: h.close }));

  const follow = async () => {
    const f = await toggleWatch(symbol);
    onToggleFollow(symbol, f);
    toast.success(f ? `${symbol} ajouté à vos valeurs suivies` : `${symbol} retiré de vos valeurs suivies`);
  };

  return (
    <div
      data-testid="brvm-stock-sheet-backdrop"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(2,10,6,0.75)", backdropFilter: "blur(5px)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
    >
      <div
        data-testid="brvm-stock-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 560, maxHeight: "88vh", overflowY: "auto",
          background: "linear-gradient(180deg, #06281A, #04150D)", borderRadius: "20px 20px 0 0",
          border: "1px solid rgba(16,185,129,0.3)", borderBottom: "none", padding: "16px 16px calc(18px + env(safe-area-inset-bottom))",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 40, height: 40, borderRadius: 10, background: tileColor(symbol), color: "#fff", fontWeight: 900, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {tileInitials(s?.short_name, symbol)}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#FFFFFF" }}>{s?.short_name || symbol}</div>
            <div style={{ fontSize: 10.5, color: "#9DB4A6" }}>{symbol}{s?.sector ? ` · ${s.sector}` : ""}</div>
          </div>
          <button data-testid="brvm-sheet-close" aria-label="Fermer" onClick={onClose} style={{ background: "rgba(255,255,255,0.06)", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <X size={16} color="#CFE5D6" />
          </button>
        </div>

        {error && <div style={{ padding: "28px 0", textAlign: "center", color: "#9DB4A6", fontSize: 13 }}>Données momentanément indisponibles.</div>}
        {!error && !s && <div style={{ padding: "28px 0", textAlign: "center", color: "#9DB4A6", fontSize: 13 }}>Chargement…</div>}

        {s && (
          <>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 14 }}>
              <span data-testid="brvm-sheet-price" style={{ fontSize: 28, fontWeight: 900, color: "#FFFFFF", letterSpacing: -0.5 }}>
                {fmtNum(s.close)} <span style={{ fontSize: 13, fontWeight: 700, color: "#9DB4A6" }}>FCFA</span>
              </span>
              <span style={{ fontSize: 14.5, fontWeight: 800, color: deltaColor(s.delta_pct) }}>
                {fmtPct(s.delta_pct)} {deltaArrow(s.delta_pct)}
              </span>
            </div>
            {data?.updated_at && (
              <div style={{ fontSize: 10.5, color: "#6B7F72", marginTop: 3 }}>
                Dernière mise à jour : {new Date(data.updated_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginTop: 14 }}>
              <Stat label="Ouverture" value={`${fmtNum(s.open)} FCFA`} unavailable={!s.open} />
              <Stat label="Cours veille" value={`${fmtNum(s.prev_close)} FCFA`} unavailable={s.prev_close == null} />
              <Stat label="Volume" value={fmtNum(s.volume)} unavailable={s.volume == null} />
              <Stat label="Plus haut / bas" unavailable />
            </div>

            <div style={{ ...CARD, marginTop: 12, padding: "12px 10px 8px" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#CFE5D6", marginBottom: 6, padding: "0 4px" }}>Historique du cours de clôture</div>
              <BrvmChart points={chartPoints} period="1M" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
              <button
                data-testid="brvm-sheet-follow-btn"
                onClick={follow}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7, height: 44, borderRadius: 999,
                  border: followed ? "1px solid #F59E0B" : "none", cursor: "pointer", fontSize: 12.5, fontWeight: 800,
                  background: followed ? "rgba(245,158,11,0.14)" : "linear-gradient(135deg,#10B981,#0E7A4F)",
                  color: followed ? "#F59E0B" : "#fff",
                }}
              >
                <Star size={15} fill={followed ? "#F59E0B" : "none"} /> {followed ? "Valeur suivie" : "Suivre cette valeur"}
              </button>
              <button
                data-testid="brvm-sheet-alert-btn"
                onClick={() => toast.info("Les alertes de variation arrivent bientôt sur AYA.")}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, height: 44, borderRadius: 999, border: "1px solid rgba(16,185,129,0.4)", background: "rgba(6,40,26,0.7)", color: "#CFE5D6", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}
              >
                <Bell size={15} /> Alerte de variation
              </button>
            </div>

            <div style={{ fontSize: 11, color: "#6B7F72", lineHeight: 1.5, marginTop: 12 }}>
              {s.name}{s.sector ? ` — Secteur ${s.sector}.` : "."} Société cotée à la Bourse Régionale des Valeurs Mobilières (BRVM), Abidjan.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
